import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  Attachment,
  DocumentationVersion,
  History,
  HistorySourceableTypeEnum,
  SortDirectionEnum,
  useEdmDocumentationActivityHistoriesQuery,
  useEdmDocumentationVersionsQuery,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import FeatureLockBanner from '@lib/components/FeatureLockBanner';
import useDocumentationHelpPopupProps from '@lib/utils/documentation/useDocumentationHelpPopupProps';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import getFullName from '@lib/utils/getFullName';
import SystemLogo from '@lib/assets/images/datizy-logo-avatar.jpg';
import { isUpgradePlanRequiredError } from '@lib/utils/graphQLErrors';
import { PER_PAGE_KEY_DEFAULT } from '@lib/enums/urls';
import { PlanState } from '@lib/enums/plan';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import HistoryCard from './HistoryCard/HistoryCard';
import HistoryUpgradeRequiredState from './HistoryUpgradeRequiredState';
import listStyles from './DocumentViewHistory.module.scss';
import getActivityEntryInfo from './utils/getActivityEntryInfo';
import getAvatarUrl from './utils/getAvatarUrl';
import getVersionActionLabel from './utils/getVersionActionLabel';
import useInfinitePagination from './utils/useInfinitePagination';

interface Props {
  documentationId: string;
  isFullAdmin?: boolean;
  isViewLocked?: boolean;
  filePermissions: { allowDownload: boolean; allowPrint: boolean };
}

const VERSIONS_LIMIT = 100;

export default function HistoryActivityTab({
  documentationId,
  isFullAdmin,
  isViewLocked,
  filePermissions,
}: Props) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  // Holds the whole version (not just its attachment) so the open can be
  // recorded as a view of that specific version.
  const [fileViewerVersion, setFileViewerVersion] =
    useState<DocumentationVersion | null>(null);
  const { handleOpenPlanModal }: ContextProps = useOutletContext();
  const documentationHelpPopup = useDocumentationHelpPopupProps();

  const { data, loading, previousData, error } =
    useEdmDocumentationActivityHistoriesQuery({
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      // For locked plans the activity-histories resolver is BE-gated; by
      // design the Activity tab falls back to listing versions (same query
      // as the Versions sub-tab) so users still see the document timeline.
      skip: !!isViewLocked,
      variables: {
        sourceableId: documentationId,
        sourceableType: HistorySourceableTypeEnum.Documentation,
        scope: 'activity',
        page,
        limit: +PER_PAGE_KEY_DEFAULT,
        sorts: { occurredAt: SortDirectionEnum.Desc },
      },
    });

  const isUpgradeRequired = isUpgradePlanRequiredError(error);

  const {
    data: versionsData,
    loading: versionsLoading,
    previousData: versionsPreviousData,
  } = useEdmDocumentationVersionsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      documentationId,
      // When locked, paginate versions like the Versions tab; otherwise
      // fetch a large batch just to populate the version-number lookup map.
      page: isViewLocked ? page : 1,
      limit: isViewLocked ? +PER_PAGE_KEY_DEFAULT : VERSIONS_LIMIT,
      sorts: { createdAt: SortDirectionEnum.Desc },
    },
  });

  const versionsMap = useMemo(() => {
    const map = new Map<number, DocumentationVersion>();
    const versions = (_get(versionsData, 'data.collection', []) ||
      []) as DocumentationVersion[];
    versions.forEach((v) => {
      if (v.versionNumber != null) {
        map.set(v.versionNumber, v);
      }
    });
    return map;
  }, [versionsData]);

  const queryData = data || previousData;
  const newItems = useMemo(
    () => (_get(queryData, 'data.collection', []) || []) as History[],
    [queryData],
  );
  const totalPages = _get(queryData, 'data.metadata.totalPages', 1) as number;
  const firstLoading = loading && !previousData;

  const { items: allItems, hasMore } = useInfinitePagination<History>({
    newItems,
    totalPages,
    currentPage: page,
    enabled: !isViewLocked,
  });

  // ── Locked-plan branch: render versions list as the activity timeline ──
  const lockedQueryData = versionsData || versionsPreviousData;
  const newVersions = useMemo(
    () =>
      (_get(lockedQueryData, 'data.collection', []) ||
        []) as DocumentationVersion[],
    [lockedQueryData],
  );
  const lockedTotalPages = _get(
    lockedQueryData,
    'data.metadata.totalPages',
    1,
  ) as number;
  const lockedFirstLoading =
    !!isViewLocked && versionsLoading && !versionsPreviousData;

  const { items: accumulatedVersions, hasMore: lockedHasMore } =
    useInfinitePagination<DocumentationVersion>({
      newItems: newVersions,
      totalPages: lockedTotalPages,
      currentPage: page,
      enabled: !!isViewLocked,
    });

  if (isViewLocked) {
    const onUpgrade = () =>
      handleOpenPlanModal(PlanState.Locked, [
        CompanyPlanUpgradeFeatureEnum.Documentation,
      ]);
    const banner = isFullAdmin ? (
      <FeatureLockBanner
        bannerId="documentation-history-activity"
        title={t('advanced-activity-tracking-locked')}
        description={t('advanced-activity-tracking-locked-text')}
        helpPopup={documentationHelpPopup}
        onUpgrade={onUpgrade}
      />
    ) : null;

    if (lockedFirstLoading) {
      return (
        <>
          {banner}
          <CircleLoader />
        </>
      );
    }

    if (!accumulatedVersions.length && !versionsLoading) {
      return (
        <>
          {banner}
          <ListEmptyState title={t('no-data-yet')} text="" />
        </>
      );
    }

    return (
      <CircleLoaderWrapper
        isLoading={versionsLoading}
        type={CircleLoaderWrapperType.blur}
      >
        {banner}
        <div className={listStyles.list}>
          {accumulatedVersions.map((version) => {
            const { createdBy, versionNumber, attachmentAttached } = version;
            return (
              <HistoryCard
                key={version.id}
                userName={createdBy ? getFullName(createdBy) : ''}
                avatarUrl={getAvatarUrl(createdBy)}
                actionLabel={getVersionActionLabel(versionNumber, t)}
                date={version.createdAt}
                indentBody={false}
              >
                <DocumentationPreview
                  attachment={attachmentAttached}
                  notes={version.notes || undefined}
                  view={DocumentationPreviewEnum.DocumentRowCard}
                  actions={
                    <IconButton
                      icon="visibility"
                      symbolsOutlined
                      type={IconButtonTypes.PrimaryOutlined}
                      onClick={() => setFileViewerVersion(version)}
                    />
                  }
                />
              </HistoryCard>
            );
          })}
          {lockedHasMore && (
            <LinkComponent
              size={LinkSizes.small}
              type={LinkTypes.secondaryGray}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('show-more')}
            </LinkComponent>
          )}
        </div>
        <FileViewerModal
          attachment={fileViewerVersion?.attachmentAttached as Attachment}
          documentationVersionId={fileViewerVersion?.id}
          isOpen={fileViewerVersion !== null}
          onClose={() => setFileViewerVersion(null)}
          useDownloadTool={filePermissions.allowDownload}
          usePrintTool={filePermissions.allowPrint}
        />
      </CircleLoaderWrapper>
    );
  }

  if (isUpgradeRequired) {
    return <HistoryUpgradeRequiredState />;
  }

  if (firstLoading) return <CircleLoader />;

  if (!allItems.length && !loading) {
    return <ListEmptyState title={t('no-data-yet')} text="" />;
  }

  return (
    <CircleLoaderWrapper
      isLoading={loading}
      type={CircleLoaderWrapperType.blur}
    >
      <div className={listStyles.list}>
        {allItems.map((history) => {
          const { user, system } = history;
          const isSystem = system && !user;
          let userName = '';
          if (isSystem) userName = t('datizy-system');
          else if (user) userName = getFullName(user);
          const avatarUrl = isSystem ? SystemLogo : getAvatarUrl(user);

          const { actionLabel, indentBody, bodyContent, versionNumber } =
            getActivityEntryInfo(history, t);

          const version =
            versionNumber != null ? versionsMap.get(versionNumber) : undefined;
          const versionAttachment = version?.attachmentAttached;

          return (
            <HistoryCard
              key={history.id}
              userName={userName}
              avatarUrl={avatarUrl}
              actionLabel={actionLabel}
              date={history.occurredAt}
              indentBody={indentBody}
            >
              {versionAttachment ? (
                <DocumentationPreview
                  attachment={versionAttachment}
                  notes={version?.notes || undefined}
                  view={DocumentationPreviewEnum.DocumentRowCard}
                  actions={
                    <IconButton
                      icon="visibility"
                      symbolsOutlined
                      type={IconButtonTypes.PrimaryOutlined}
                      onClick={() => setFileViewerVersion(version ?? null)}
                    />
                  }
                />
              ) : (
                bodyContent
              )}
            </HistoryCard>
          );
        })}
        {hasMore && (
          <LinkComponent
            size={LinkSizes.small}
            type={LinkTypes.secondaryGray}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('show-more')}
          </LinkComponent>
        )}
      </div>
      <FileViewerModal
        attachment={fileViewerVersion?.attachmentAttached as Attachment}
        documentationVersionId={fileViewerVersion?.id}
        isOpen={fileViewerVersion !== null}
        onClose={() => setFileViewerVersion(null)}
        useDownloadTool={filePermissions.allowDownload}
        usePrintTool={filePermissions.allowPrint}
      />
    </CircleLoaderWrapper>
  );
}
